'use client';
import useWebAnimations from '@wellyshen/use-web-animations';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useIntersection } from 'react-use';
import type { ChatScriptOperationWithIndex } from '~/app/_types/chat-script';
import { Box } from '@styled-system/jsx';

type Props = {
  operation: ChatScriptOperationWithIndex;
  onShown?: (
    operation: ChatScriptOperationWithIndex,
    containerElement: HTMLDivElement | null
  ) => void;
  children: ReactNode;
};

export const OperationAnimator = ({ operation, onShown, children }: Props) => {
  const { ref, animate } = useWebAnimations<HTMLDivElement>();
  // 画面表示時にアニメーションを実行するかどうか(最初の要素だけはデフォルトでtrueにする)
  const [isEnter, setEnter] = useState(operation.index === 0);
  // 画像系は50%がInViewしたら表示されたら表示する
  const intersectionThreshold =
    operation.operationType === 'show_image_op' ||
    operation.operationType === 'post_image_op' ||
    // 100文字以上のテキストも50%がInViewしたら表示する
    ((operation.operationType === 'show_text_op' ||
      operation.operationType === 'post_message_op') &&
      operation.text.length > 100)
      ? 0.5
      : 1;
  const intersection = useIntersection(ref, {
    root: null,
    rootMargin: `0px 0px -30% 0px`,
    threshold: intersectionThreshold,
  });

  const position = useMemo(() => {
    if (operation.hasCharacter === false) return;

    return operation.character.position;
  }, [operation]);

  useEffect(() => {
    if (intersection?.isIntersecting === true && isEnter === false) {
      setEnter(true);
      animate({
        keyframes: [{ opacity: 1, transform: 'translateX(0px)' }],
        animationOptions: {
          duration: 200,
          direction: 'normal',
          fill: 'forwards',
          easing: 'ease-out',
        },
      });
      onShown?.(operation, ref.current);
    }
  }, [animate, intersection?.isIntersecting, isEnter, onShown, operation, ref]);

  // 最初に表示される要素か
  const isFirstOp = operation.index === 0;
  const initialTransform = useMemo(() => {
    if (!position || isFirstOp) return;

    return position === 'left' ? 'translateX(-10px)' : 'translateX(10px)';
  }, [isFirstOp, position]);

  // 最初の表示要素はopacityを1にする
  const initialOpacity = isFirstOp ? 1 : 0;

  return (
    <Box
      ref={ref}
      w="100%"
      opacity={initialOpacity}
      style={{
        transform: initialTransform,
      }}
    >
      {children}
    </Box>
  );
};
